import styled from 'styled-components';
import React, { useState } from 'react';
import InputMask from 'react-input-mask';
import ErrorMessage from 'components/ui/ErrorMessage/ErrorMessage';
import {
  InputTextContent,
  InputTextFull,
  InputTextLabel,
  InputTextPlaceholder,
  InputTextPrefix,
  InputTextWrapper,
  TextInputCell,
  TooltipWrapper,
} from '../styles';

interface InputOptions {
  maxLength?: number;
  defaultValue?: any;
  onChange?: (e: any) => void;
  onKeyDown?: (e: any) => void;
  inputMode?: any;
  pattern?: string;
}

interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  name?: string;
  disabled?: boolean;
  type?: string;
  onChange?: (e: any) => void;
  tooltip?: React.ReactNode;
  errorMessage?: string;
  onKeyDown?: (e: any) => void;
  bottomSpacing?: boolean;
  icon?: React.ReactNode;
  pattern?: string;
  prefix?: string;
  register?: () => void;
  inputRef?: React.Ref<any>;
  hasValue?: boolean;
  hasError?: boolean;
  error?: Record<string, unknown>;
  defaultValue?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  placeholder?: string;
  bold?: boolean;
  inputMode?: any;
  mask?: string;
  maskPlaceholder?: string;
  dataTestId?: string;
  validate?: (e: any) => void;
}

const InputTextWithMask = (props: Props) => {
  const {
    label = '',
    name = '',
    disabled,
    type = 'text',
    onChange,
    tooltip = null,
    errorMessage = null,
    onKeyDown,
    bottomSpacing = true,
    icon,
    pattern,
    prefix,
    inputRef,
    hasValue,
    hasError,
    error,
    defaultValue,
    onFocus,
    onBlur,
    placeholder,
    bold = true,
    inputMode,
    mask,
    maskPlaceholder = ' ',
    dataTestId,
    validate,
  } = props;

  const opts: InputOptions = {};

  if (defaultValue) {
    opts.defaultValue = defaultValue;
  }

  if (onChange) {
    opts.onChange = (e) => onChange(e);
  }

  if (onKeyDown) {
    opts.onKeyDown = (e) => onKeyDown(e);
  }

  if (inputMode) {
    opts.inputMode = inputMode;
  }

  if (pattern) {
    opts.pattern = pattern;
  }

  const [isFocused, setFocused] = useState(false);

  const handleFocus = () => {
    setFocused(true);
    if (onFocus) {
      onFocus();
    }
  };

  const handleBlur = () => {
    setFocused(false);
    if (onBlur) {
      onBlur();
    }
  };

  return (
    <InputTextFull data-private="lipsum" className={`${bottomSpacing ? 'InputTextBottomSpacing' : ''}`}>
      <InputTextContent>
        <TextInputCell>
          <InputTextWrapperExt>
            <InputTextLabel
              className={`${icon ? 'InputTextLabelWithIcon' : ''} ${
                hasValue || isFocused ? 'InputTextLabelIsFocused' : ''
              }`}
            >
              {label}
            </InputTextLabel>
            {prefix && (
              <InputTextPrefix className={`${hasValue || isFocused ? 'InputTextPrefixShow' : ''}`}>
                {prefix}
              </InputTextPrefix>
            )}
            {placeholder && (
              <InputTextPlaceholder className={`${!hasValue && isFocused ? 'InputTextPlaceholderShow' : ''}`}>
                {placeholder}
              </InputTextPlaceholder>
            )}
            <InputMask
              className={`InputText InputTextWithAnimatedLabel ${prefix ? 'InputTextHasPrefix' : ''} ${
                icon ? 'InputTextWithIconBig' : ''
              } ${hasError ? 'InputTextInvalid' : ''} ${bold ? 'InputTextBold' : ''}`}
              mask={mask}
              maskPlaceholder={maskPlaceholder}
              type={type}
              name={name}
              id={name}
              disabled={disabled}
              ref={inputRef}
              autoComplete="off"
              onFocus={() => handleFocus()}
              onBlur={() => handleBlur()}
              data-testid={dataTestId}
              validate={validate}
              {...opts}
            />

            {tooltip ? <TooltipWrapper>{tooltip}</TooltipWrapper> : null}
          </InputTextWrapperExt>
          <ErrorMessage
            className="InputTextError"
            error={error}
            message={errorMessage || `Please enter ${label || ''}`}
          />
        </TextInputCell>
      </InputTextContent>
    </InputTextFull>
  );
};

export default InputTextWithMask;

const InputTextWrapperExt = styled(InputTextWrapper)`
  .InputText {
    padding: 0.9em;
    width: 100%;
    -webkit-appearance: none;
    appearance: none;
    box-shadow: none;
    outline: none;
    line-height: normal;
    color: #17233c;
    background-color: #fff;
    &::placeholder {
      color: #b4b4b4;
    }

    border: solid 1px #dbdfe5;
    border-radius: 6px;
    height: 50px;
    transition: all 200ms;

    &.InputTextWithAnimatedLabel {
      font-size: 16px;
      padding-top: 32px;
      padding-left: 10px;
      &:-webkit-autofill,
      &:-webkit-autofill:hover,
      &:-webkit-autofill:active,
      &:-webkit-autofill:focus,
      &:-webkit-autofill::first-line {
        -webkit-text-fill-color: black;
        -webkit-box-shadow: 0 0 0px 1px rgba(0, 0, 0, 0.01) inset;
        -webkit-box-shadow: 0 0 0 1000px #fff inset;
        transition: background-color 5000s ease-in-out 0s;
        font-weight: 600;
      }
    }

    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:active,
    &:-webkit-autofill:focus,
    &:-webkit-autofill::first-line {
      -webkit-text-fill-color: black;
      -webkit-box-shadow: 0 0 0px 1px rgba(0, 0, 0, 0.01) inset;
      -webkit-box-shadow: 0 0 0 1000px #fff inset;
      transition: background-color 5000s ease-in-out 0s;
    }

    &:-webkit-autofill::first-line {
      font-size: 16px;
      font-weight: 400;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
      background-color: rgb(242, 242, 242);
      border-color: rgb(230, 230, 230);
    }

    &:focus {
      border: solid 1px #8e9697;
      &.InputTextInvalid {
        border: solid 1px #bc350a;
      }
    }

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    /* Firefox */
    &[type='number'] {
      -moz-appearance: textfield;
    }

    &::-webkit-validation-bubble-message {
      display: none !important;
    }

    &.InputTextInvalid {
      border-color: #bc350a;
      color: #bc350a;
    }

    &.InputTextInvalid ~ i:before {
      color: #bc350a;
    }

    &.InputTextHasPrefix {
      padding-left: 22px;
    }

    &.InputTextWithIconBig {
      padding-left: 3em;
    }

    &.InputTextBold {
      font-weight: 600;
    }
  }
`;
